import { useMutation } from '@tanstack/react-query';
import { cloneDeep } from 'lodash-es';
import { useEffect, useMemo } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useAppContext } from 'src/contexts/AppContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { Checkbox } from 'src/core/interim/Checkbox';
import { PosFormField } from 'src/core/POS/PosFormField';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { PosTextField } from 'src/core/POS/PosTextField';
import { Button, Stack } from 'src/core/ui';
import { usePurchaseOrderSettings } from 'src/hooks/api/usePurchaseOrderSettings';
import { useSyncCenterSettings } from 'src/hooks/api/useSyncCenterSetting';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import {
  Feature,
  SellerAccountClient,
  SyncCenterSettings,
} from 'src/WebApiController';

import { SyncCenterFieldValues } from '../SyncCenter/SyncCenter.types';
import {
  setValueAsNumber,
  toSyncCenterFieldValues,
  toSyncCenterSettings,
} from '../SyncCenter/SyncCenter.utils';
import * as styles from './PurchaseOrderSetting.css';

export function PurchaseOrderGeneralSettingForm() {
  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog } = useClientLogContext();

  const query = useSyncCenterSettings();
  const hasAutoGeneratePONumbersFeature = useUserHasFeature(
    Feature.AutoGeneratePONumbers
  );
  const hasParkingEventsFilterSetting = useUserHasFeature(
    Feature.ParkingEventsFilterSetting
  );

  const purchaseOrderSettings = usePurchaseOrderSettings();

  const greaterThanZeroRequired = useContent(ContentId.GreaterThanZeroRequired);

  const formDefaultValues = useMemo(() => {
    if (query.data) {
      return toSyncCenterFieldValues(cloneDeep(query.data));
    }
    return undefined;
  }, [query.data]);

  const mutation = useMutation({
    mutationFn: async (data: SyncCenterSettings) => {
      return new SellerAccountClient(activeAccountWebClientConfig)
        .mergeSyncCenterSettings(data)
        .then(() => query.refetch());
    },
    onError: (err: ErrorTypes, data) => {
      showErrorDialog('SellerAccountClient.mergeSyncCenterSettings', err, {
        trackErrorData: { data },
      });
    },
  });

  const { control, register, handleSubmit, reset, formState } =
    useForm<SyncCenterFieldValues>({
      defaultValues: formDefaultValues,
    });

  useEffect(() => {
    reset(formDefaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formDefaultValues]);

  if (query.isLoading) {
    return <PosSpinner />;
  }
  return (
    <>
      <div className={styles.sectionContainer}>
        <div className={styles.sectionName}>
          <Content id={ContentId.Purchase} />
        </div>
        <div className={styles.form}>
          <Stack direction="column" gap="s">
            <div className={styles.label}>
              <Content id={ContentId.InHandDaysBeforeEvent} />
            </div>
            <p className={styles.subtleText}>
              <Content id={ContentId.DefaultInHandDaysBeforeEventDescription} />
            </p>

            <PosFormField
              label={<></>}
              errors={formState.errors.inHandDaysBeforeEvent?.message}
              className={styles.textField}
            >
              <PosTextField
                disabled={mutation.isPending}
                type="number"
                {...register('inHandDaysBeforeEvent', {
                  setValueAs: setValueAsNumber,
                  validate: {
                    minValue: (value: number | null) => {
                      return value && value <= 0
                        ? greaterThanZeroRequired
                        : undefined;
                    },
                  },
                })}
              />
            </PosFormField>
          </Stack>

          <Controller
            control={control}
            name="isAutoPoQualityControlled"
            render={({ field: { value, onChange, ...field } }) => (
              <Checkbox
                disabled={mutation.isPending}
                labelPosition="right"
                label={<Content id={ContentId.EnableQualityControlForAutoPo} />}
                checked={Boolean(value)}
                onChange={(e) => {
                  onChange(e.currentTarget.checked);
                }}
                {...field}
              />
            )}
          />

          <div className={styles.footer}>
            <Button
              disabled={mutation.isPending || !formState.isDirty}
              onClick={handleSubmit((data) => {
                mutation.mutate(toSyncCenterSettings(data));
              })}
            >
              <Content id={ContentId.Save} />
            </Button>
          </div>
        </div>
      </div>
      {hasParkingEventsFilterSetting && (
        <div className={styles.sectionContainer}>
          <div className={styles.sectionName}>
            <Content id={ContentId.PurchaseCreation} />
          </div>
          <PurchaseCreationSettingsSection
            initialValue={
              purchaseOrderSettings.data?.excludeParkingEventsFromEventSearch ??
              false
            }
            mutation={purchaseOrderSettings.mutation}
            currentSettings={purchaseOrderSettings.data}
          />
        </div>
      )}
      {hasAutoGeneratePONumbersFeature && (
        <div className={styles.sectionContainer}>
          <div className={styles.sectionName}>
            <Content id={ContentId.PurchaseIdConfiguration} />
          </div>
          {purchaseOrderSettings.isLoading ? (
            <PosSpinner />
          ) : (
            <AutoGeneratePONumberSection
              initialValues={{
                isAutoGeneratedVendorOrderIdEnabled:
                  purchaseOrderSettings.data
                    ?.isAutoGeneratedVendorOrderIdEnabled ?? false,
                vendorOrderIdPrefix:
                  purchaseOrderSettings.data?.vendorOrderIdPrefix ?? '',
              }}
              mutation={purchaseOrderSettings.mutation}
              currentSettings={purchaseOrderSettings.data}
            />
          )}
        </div>
      )}
    </>
  );
}

interface PurchaseCreationSettingsFormValues {
  excludeParkingEventsFromEventSearch: boolean;
}

interface PurchaseCreationSettingsSectionProps {
  initialValue: boolean;
  mutation: ReturnType<typeof usePurchaseOrderSettings>['mutation'];
  currentSettings: ReturnType<typeof usePurchaseOrderSettings>['data'];
}

function PurchaseCreationSettingsSection({
  initialValue,
  mutation,
  currentSettings,
}: PurchaseCreationSettingsSectionProps) {
  const { control, handleSubmit, formState, reset } =
    useForm<PurchaseCreationSettingsFormValues>({
      defaultValues: {
        excludeParkingEventsFromEventSearch: initialValue,
      },
    });

  useEffect(() => {
    reset({
      excludeParkingEventsFromEventSearch: initialValue,
    });
  }, [initialValue, reset]);

  return (
    <div className={styles.form}>
      <Controller
        control={control}
        name="excludeParkingEventsFromEventSearch"
        render={({ field: { value, onChange, ...field } }) => (
          <Checkbox
            disabled={mutation.isPending}
            labelPosition="right"
            label={
              <Content id={ContentId.ExcludeParkingEventsFromEventSearch} />
            }
            checked={Boolean(value)}
            onChange={(e) => {
              onChange(e.currentTarget.checked);
            }}
            {...field}
          />
        )}
      />

      <div className={styles.footer}>
        <Button
          disabled={mutation.isPending || !formState.isDirty}
          onClick={handleSubmit((formData) => {
            mutation.mutate({
              isAutoGeneratedVendorOrderIdEnabled:
                currentSettings?.isAutoGeneratedVendorOrderIdEnabled ?? null,
              vendorOrderIdPrefix: currentSettings?.vendorOrderIdPrefix ?? null,
              excludeParkingEventsFromEventSearch:
                formData.excludeParkingEventsFromEventSearch,
            });
          })}
        >
          <Content id={ContentId.Save} />
        </Button>
      </div>
    </div>
  );
}

interface AutoGeneratePONumberFormValues {
  isAutoGeneratedVendorOrderIdEnabled: boolean;
  vendorOrderIdPrefix: string;
}

interface AutoGeneratePONumberSectionProps {
  initialValues: AutoGeneratePONumberFormValues;
  mutation: ReturnType<typeof usePurchaseOrderSettings>['mutation'];
  currentSettings: ReturnType<typeof usePurchaseOrderSettings>['data'];
}

function AutoGeneratePONumberSection({
  initialValues,
  mutation,
  currentSettings,
}: AutoGeneratePONumberSectionProps) {
  const { control, register, handleSubmit, formState, reset } =
    useForm<AutoGeneratePONumberFormValues>({
      defaultValues: initialValues,
    });

  useEffect(() => {
    reset(initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initialValues.isAutoGeneratedVendorOrderIdEnabled,
    initialValues.vendorOrderIdPrefix,
    reset,
  ]);

  const isAutoGeneratedEnabled = useWatch({
    control,
    name: 'isAutoGeneratedVendorOrderIdEnabled',
  });

  const purchaseOrderPrefixPlaceholder = useContent(
    ContentId.PurchaseOrderPrefixPlaceholder
  );

  return (
    <div className={styles.form}>
      <Stack direction="column" gap="l">
        <Stack direction="column" gap="s">
          <div className={styles.label}>
            <Content id={ContentId.AutoGeneratePurchaseOrderNumber} />
          </div>
          <p className={styles.subtleText}>
            <Content
              id={ContentId.AutoGeneratePurchaseOrderNumberDescription}
            />
          </p>

          <Controller
            control={control}
            name="isAutoGeneratedVendorOrderIdEnabled"
            render={({ field: { value, onChange, ...field } }) => (
              <Checkbox
                disabled={mutation.isPending}
                labelPosition="right"
                label={<Content id={ContentId.Enabled} />}
                checked={Boolean(value)}
                onChange={(e) => {
                  onChange(e.currentTarget.checked);
                }}
                {...field}
              />
            )}
          />
        </Stack>

        {isAutoGeneratedEnabled && (
          <Stack direction="column" gap="s">
            <div className={styles.label}>
              <Content id={ContentId.PurchaseOrderPrefix} />
            </div>
            <PosFormField label={<></>} className={styles.textField}>
              <PosTextField
                disabled={mutation.isPending}
                placeholder={purchaseOrderPrefixPlaceholder}
                maxLength={20}
                {...register('vendorOrderIdPrefix')}
              />
            </PosFormField>
          </Stack>
        )}
      </Stack>

      <div className={styles.footer}>
        <Button
          disabled={mutation.isPending || !formState.isDirty}
          onClick={handleSubmit((formData) => {
            mutation.mutate({
              isAutoGeneratedVendorOrderIdEnabled:
                formData.isAutoGeneratedVendorOrderIdEnabled,
              vendorOrderIdPrefix: formData.vendorOrderIdPrefix,
              excludeParkingEventsFromEventSearch:
                currentSettings?.excludeParkingEventsFromEventSearch ?? null,
            });
          })}
        >
          <Content id={ContentId.Save} />
        </Button>
      </div>
    </div>
  );
}
